import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import { useNotify, useQueryWithStore } from "ra-core";
import React from "react";
import { useRefresh } from "react-admin";
import { apiUrl, getUser, httpClient } from "../../dataProvider";
import { Booking } from "../../types";

interface Props {
  data: Booking;
}

const AcceptCancellationDialog: React.FC<Props> = ({ data }) => {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [categoryId, setCategoryId] = React.useState<number | "">("");
  const notify = useNotify();
  const refresh = useRefresh();

  const { data: categories }: { data?: any[] } = useQueryWithStore({
    type: "getList",
    resource: "cancelation-categories",
    payload: {
      pagination: { perPage: 1000, page: 1 },
      sort: { field: "name", order: "asc" },
      filter: {},
    },
  });

  const handleClickOpen = () => {
    setOpen(true);
    setCategoryId(data.cancelation_category?.id ?? "");
  };

  const handleClose = () => {
    setOpen(false);
    setCategoryId("");
  };

  const handleCategoryChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setCategoryId(event.target.value as number | "");
  };

  const handleConfirm = async () => {
    const reason = data.cancellation_reason ?? "";
    if (!reason.trim()) {
      notify("No cancellation reason found on booking payload.", "warning");
      return;
    }

    setLoading(true);
    try {
      const url = `${apiUrl}/bookings/${data.id}/accept-cancellation-request`;
      await httpClient(url, {
        method: "PUT",
        body: JSON.stringify({
          cancellation_reason: reason,
          cancelation_category_id: categoryId !== "" ? categoryId : undefined,
        }),
        ...getUser(),
      });
      notify(`Cancellation request accepted.`, "info");
      handleClose();
      // refresh react-admin data provider / view so status and UI update
      try {
        // refresh will re-run the current view's dataProvider getOne
        refresh();
      } catch (e) {
        // swallow refresh errors - we already notified success
        // eslint-disable-next-line no-console
        console.warn("refresh failed", e);
      }
    } catch (error: any) {
      const msg = error?.message || "Unknown error";
      notify(`Error: ${msg}`, "warning");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        style={{ marginLeft: 10 }}
        color="secondary"
        onClick={handleClickOpen}
        size={"small"}
        variant="contained"
        disabled={loading}
      >
        Accept Cancellation Request
      </Button>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle id="alert-dialog-title">Accept Cancellation?</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to accept the cancellation request for this
            booking?
          </DialogContentText>
          <div style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
            {data.cancellation_reason}
          </div>
          <FormControl fullWidth variant="outlined" style={{ marginTop: 16 }}>
            <InputLabel id="cancel-category-label">
              Cancellation Category
            </InputLabel>
            <Select
              labelId="cancel-category-label"
              value={categoryId}
              onChange={handleCategoryChange}
              label="Cancellation Category"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {categories &&
                categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Back
          </Button>
          <Button
            onClick={handleConfirm}
            color="primary"
            autoFocus
            disabled={loading}
            variant="contained"
          >
            {loading ? "Please wait..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AcceptCancellationDialog;
